(() => {
  const GT_CODE = { es: "es", pt: "pt", zh: "zh-CN" };
  const RESPOND = { en: "en", es: "es", pt: "pt", zh: "zh" };
  const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "TEXTAREA", "OPTION", "SELECT"]);
  const mem = new Map();
  const current = () => window.themeI18n?.lang?.() || "en";

  const writeCookie = (name, value) => {
    const base = `${name}=${value};path=/;max-age=31536000`;
    document.cookie = base;
    document.cookie = `${base};domain=${location.hostname}`;
    document.cookie = `${base};domain=.${location.hostname.replace(/^www\./, "")}`;
  };
  const clearCookie = (name) => {
    const kill = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = kill;
    document.cookie = `${kill};domain=${location.hostname}`;
    document.cookie = `${kill};domain=.${location.hostname.replace(/^www\./, "")}`;
  };
  const persist = (lang) => {
    if (lang === "en" || !GT_CODE[lang]) {
      clearCookie("googtrans");
      return;
    }
    writeCookie("googtrans", `/en/${GT_CODE[lang]}`);
  };

  const lockUi = () => {
    document.querySelectorAll([
      "[data-i18n]", "[data-i18n-html]", "[data-i18n-placeholder]", "[data-i18n-aria]", "[data-i18n-nav]",
      "[data-money]", "[data-currency-switch]", "[data-lang-switch]",
      "[data-lang-pick]", "[data-currency-pick]", "[data-pref-toggle]",
      "[data-buy-now]", "[data-add-cart]", "[data-add-compare]",
      ".pref-switchers", ".pref-menu", ".theme-switch", ".brand-logo", ".logo",
      ".social-links", ".social-link",
      ".price", ".price-compare", ".price-sale", ".badge", ".stock-note"
    ].join(",")).forEach((el) => {
      el.classList.add("notranslate");
      el.setAttribute("translate", "no");
    });
    document.querySelectorAll("a[href^='tel:'], a[href^='mailto:']").forEach((el) => {
      const text = (el.textContent || "").trim();
      if (/@/.test(text) || /^[\d+\s().-]+$/.test(text)) {
        el.classList.add("notranslate");
        el.setAttribute("translate", "no");
      }
    });
  };

  const syncChat = (lang) => {
    const code = RESPOND[lang] || "en";
    window.__respond_settings = Object.assign({}, window.__respond_settings || {}, {
      language: code,
      autoDetectLanguage: false
    });
    try { window.$respond?.do?.("chat:reinitialize"); } catch (e) {}
  };

  const digest = (text) => {
    let h = 5381;
    for (let i = 0; i < text.length; i++) h = ((h << 5) + h) ^ text.charCodeAt(i);
    return (h >>> 0).toString(36);
  };
  const storeKey = (lang, text) => `sf-tx:${lang}:${text.length}:${digest(text)}`;
  const memKey = (lang, text) => `${lang}\n${text}`;

  const cached = (lang, text) => {
    if (!text) return "";
    const exact = mem.get(memKey(lang, text));
    if (exact) return exact;
    const trimmed = text.trim();
    if (trimmed !== text) {
      const hit = mem.get(memKey(lang, trimmed));
      if (hit) return hit;
    }
    try {
      const hit = localStorage.getItem(storeKey(lang, trimmed)) || sessionStorage.getItem(storeKey(lang, trimmed));
      if (hit) {
        mem.set(memKey(lang, trimmed), hit);
        return hit;
      }
    } catch (e) {}
    return "";
  };

  const remember = (lang, text, translated) => {
    const key = String(text || "").trim();
    if (!key || !translated) return;
    mem.set(memKey(lang, key), translated);
    try { localStorage.setItem(storeKey(lang, key), translated); } catch (e) {
      try { sessionStorage.setItem(storeKey(lang, key), translated); } catch (err) {}
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const fetchJson = async (url, ms = 5000) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    try {
      const res = await fetch(url, { signal: ctrl.signal });
      if (!res.ok) throw new Error("translate failed");
      return await res.json();
    } finally {
      clearTimeout(timer);
    }
  };

  const gtx = async (text, lang) => {
    const tl = GT_CODE[lang] || lang;
    const chunk = text.length > 1400 ? text.slice(0, 1400) : text;
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${encodeURIComponent(tl)}&dt=t&q=${encodeURIComponent(chunk)}`;
    const data = await fetchJson(url);
    return (data[0] || []).map((row) => row[0]).join("");
  };

  const translateOne = async (text, lang) => {
    const hit = cached(lang, text);
    if (hit) return hit;
    const out = String(await gtx(text, lang) || "").trim();
    if (out) remember(lang, text, out);
    return out;
  };

  const locked = (el) => !!(el && el.closest(".notranslate, [translate='no'], [data-money], .pref-switchers, .theme-switch, dialog"));

  const textNodes = () => {
    const nodes = [];
    if (!document.body) return nodes;
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || SKIP.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
        if (locked(parent)) return NodeFilter.FILTER_REJECT;
        if (node.sfEn != null) return NodeFilter.FILTER_ACCEPT;
        const value = node.textContent || "";
        if (value.trim().length < 2) return NodeFilter.FILTER_REJECT;
        if (!/[\p{L}]/u.test(value)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    while (walker.nextNode()) nodes.push(walker.currentNode);
    return nodes;
  };

  const attrTargets = () => [...document.querySelectorAll("[placeholder], [aria-label], [title]")].filter((el) => {
    if (locked(el)) return false;
    if (el.hasAttribute("data-i18n-placeholder") || el.hasAttribute("data-i18n-aria")) return false;
    return true;
  });

  const written = (source, next) => {
    if (!next) return source;
    return source === source.trim() ? next : source.replace(source.trim(), next);
  };

  const paint = (lang) => {
    textNodes().forEach((node) => {
      if (node.sfEn == null) node.sfEn = node.textContent;
      const source = node.sfEn;
      if (!source) return;
      const value = lang === "en" ? source : written(source, cached(lang, source));
      if (value && node.textContent !== value) node.textContent = value;
    });
    attrTargets().forEach((el) => {
      ["placeholder", "aria-label", "title"].forEach((name) => {
        const key = `sfEn_${name}`;
        if (el[key] == null) {
          const value = el.getAttribute(name);
          if (value) el[key] = value;
        }
        const source = el[key];
        if (!source) return;
        const value = lang === "en" ? source : written(source, cached(lang, source));
        if (value && el.getAttribute(name) !== value) el.setAttribute(name, value);
      });
    });
    if (document.title) {
      if (!document.sfEnTitle) document.sfEnTitle = document.title;
      const value = lang === "en" ? document.sfEnTitle : written(document.sfEnTitle, cached(lang, document.sfEnTitle));
      if (value && document.title !== value) document.title = value;
    }
  };

  const collect = () => {
    const unique = [];
    const seen = new Set();
    const add = (text) => {
      const key = String(text || "").trim();
      if (!key || key.length < 2 || !/[A-Za-z]/.test(key) || seen.has(key)) return;
      seen.add(key);
      unique.push(key);
    };
    textNodes().forEach((node) => {
      if (node.sfEn == null) node.sfEn = node.textContent;
      add(node.sfEn);
    });
    attrTargets().forEach((el) => {
      ["placeholder", "aria-label", "title"].forEach((name) => {
        const key = `sfEn_${name}`;
        const live = el.getAttribute(name);
        if (el[key] == null && live) el[key] = live;
        add(el[key]);
      });
    });
    if (document.title) {
      document.sfEnTitle = document.sfEnTitle ?? document.title;
      add(document.sfEnTitle);
    }
    return unique;
  };

  let gen = 0;

  const fill = async (lang, mine) => {
    const pending = collect().filter((text) => !cached(lang, text));
    const batch = 16;
    for (let i = 0; i < pending.length; i += batch) {
      if (mine !== gen || current() !== lang) return;
      await Promise.all(pending.slice(i, i + batch).map((text) => translateOne(text, lang).catch(() => "")));
      if (mine !== gen || current() !== lang) return;
      paint(lang);
    }
  };

  const combo = () => document.querySelector("select.goog-te-combo");

  const fireCombo = (lang) => {
    const select = combo();
    if (!select) return false;
    const wanted = lang === "en"
      ? ([...select.options].find((opt) => opt.value === "" || opt.value === "en")?.value ?? "")
      : (GT_CODE[lang] || lang);
    select.value = wanted;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    return select.value === wanted;
  };

  let widgetWait = null;
  const bootWidget = () => {
    try {
      new window.google.translate.TranslateElement({
        pageLanguage: "en",
        includedLanguages: "en,es,pt,zh-CN",
        autoDisplay: false
      }, "google_translate_element");
    } catch (e) {}
  };
  const loadWidget = () => {
    if (combo()) return Promise.resolve(true);
    if (widgetWait) return widgetWait;
    widgetWait = new Promise((resolve) => {
      const finish = () => resolve(!!combo());
      window.__sfGtInit = function () {
        bootWidget();
        setTimeout(finish, 20);
      };
      if (window.google?.translate?.TranslateElement) {
        window.__sfGtInit();
        return;
      }
      if (window.__sfGtPending && window.google?.translate?.TranslateElement) {
        window.__sfGtInit();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
      setTimeout(finish, 2500);
    });
    return widgetWait;
  };

  const useGoogle = async (lang) => {
    const ready = await loadWidget();
    if (!ready) return false;
    for (let i = 0; i < 20; i++) {
      if (fireCombo(lang)) return true;
      await sleep(25);
    }
    return fireCombo(lang);
  };

  const to = (lang) => {
    const mine = ++gen;
    persist(lang);
    lockUi();
    syncChat(lang);
    paint(lang);
    if (lang === "en") return;
    fill(lang, mine);
  };

  const prefetch = () => {
    const texts = collect();
    ["es", "pt", "zh"].forEach((lang) => {
      const pending = texts.filter((text) => !cached(lang, text)).slice(0, 80);
      const run = async () => {
        for (let i = 0; i < pending.length; i += 3) {
          await Promise.all(pending.slice(i, i + 3).map((text) => translateOne(text, lang).catch(() => "")));
        }
      };
      run();
    });
  };

  let timer = 0;
  const watch = () => {
    if (!document.body) return;
    new MutationObserver((records) => {
      const lang = current();
      if (lang === "en") return;
      const added = records.some((record) => record.addedNodes && record.addedNodes.length);
      if (!added) return;
      lockUi();
      paint(lang);
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (current() !== lang) return;
        fill(lang, gen);
      }, 180);
    }).observe(document.body, { childList: true, subtree: true });
  };

  const start = () => {
    const lang = current();
    persist(lang);
    lockUi();
    syncChat(lang);
    paint("en");
    if (lang !== "en") to(lang);
    if ("requestIdleCallback" in window) requestIdleCallback(prefetch, { timeout: 800 });
    else setTimeout(prefetch, 400);
    watch();
  };

  window.themeTranslate = { persist, lockUi, syncChat, start, to };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
